import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { ThumbsUp, PartyPopper, Heart, Lightbulb, HelpCircle, HandHeart } from "lucide-react";

interface EmotionReactionChartProps {
  reactions: {
    likes: number;
    celebrates: number;
    supports: number;
    loves: number;
    insightful: number;
    curious: number;
    totalReactions: number;
  };
}

export const EmotionReactionChart = ({ reactions }: EmotionReactionChartProps) => {
  if (!reactions || reactions.totalReactions === 0) {
    return null;
  }

  const data = [
    { 
      name: 'Like', 
      value: reactions.likes, 
      percentage: Math.round((reactions.likes / reactions.totalReactions) * 100),
      color: '#3b82f6',
      icon: ThumbsUp
    },
    { 
      name: 'Celebrate', 
      value: reactions.celebrates, 
      percentage: Math.round((reactions.celebrates / reactions.totalReactions) * 100),
      color: '#10b981',
      icon: PartyPopper
    },
    { 
      name: 'Support', 
      value: reactions.supports, 
      percentage: Math.round((reactions.supports / reactions.totalReactions) * 100),
      color: '#8b5cf6',
      icon: HandHeart
    },
    { 
      name: 'Love', 
      value: reactions.loves, 
      percentage: Math.round((reactions.loves / reactions.totalReactions) * 100),
      color: '#ef4444',
      icon: Heart
    },
    { 
      name: 'Insightful', 
      value: reactions.insightful, 
      percentage: Math.round((reactions.insightful / reactions.totalReactions) * 100),
      color: '#f59e0b',
      icon: Lightbulb
    },
    { 
      name: 'Curious', 
      value: reactions.curious, 
      percentage: Math.round((reactions.curious / reactions.totalReactions) * 100),
      color: '#06b6d4',
      icon: HelpCircle
    },
  ].filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border rounded-lg p-3 shadow-lg">
          <p className="font-semibold">{data.name}</p>
          <p className="text-sm text-muted-foreground">
            {data.value.toLocaleString()} ({data.percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Rozkład Emocji w Reakcjach</CardTitle>
        <CardDescription>
          Analiza typów reakcji na posty pracowników
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ percentage }) => percentage > 5 ? `${percentage}%` : ''}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          <div className="space-y-3">
            {data.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold">{item.value.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">{item.percentage}%</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <p className="text-sm text-muted-foreground">
            <span className="font-semibold">Insight:</span>{' '}
            {data[0] && (
              <>
                Najczęstszą reakcją jest <span className="font-medium">{data[0].name}</span> ({data[0].percentage}%).
                {data[0].name === 'Insightful' || data[0].name === 'Curious' 
                  ? ' To świetny znak - treści są postrzegane jako wartościowe i angażujące intelektualnie.'
                  : data[0].name === 'Celebrate' || data[0].name === 'Support'
                  ? ' To pozytywny sygnał - posty budują społeczność i wsparcie.'
                  : ' To standardowy poziom zaangażowania.'}
              </>
            )}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
